import { DateTime } from 'luxon';
import { RRule } from 'rrule';

import { EventSchedule, Schedule } from './ScheduleInterface';
import { T } from './translations';

export const SINGLE_EVENT = 'single';
export const WEEKLY_EVENT = 'weekly';

/**
 * Check if weekday array matches template
 *
 * @param {Array} weekdays
 * @param {Array} template
 */
const checkForWeekdayTemplate = (weekdays: number[], template: number[]) => {
  /** If the template length doesn't match, no need to continue. Just return false. */
  if (template.length !== weekdays.length) {
    return false;
  }

  let templateMatches = true;

  weekdays.forEach((day) => {
    if (template.indexOf(day) < 0) {
      templateMatches = false;
    }
  });

  return templateMatches;
};

/**
 * Converts weekdays from UTC to their local values. By default the API returns weekdays & dailyStartTime
 * in UTC, so in order for this to display correctly to users we need to convert it to their local browser's timezone.
 * @param {number[]} weekdays
 * @param {string} startTime
 * @returns {number[]}
 */
const convertWeekdaysToLocal = (weekdays: number[] = [], startTime: string = ''): number[] => {
  return weekdays.map((day: number): number => {
    const convertedWeekday = DateTime.fromFormat(
      startTime.substring(0, startTime.length - 1),
      'HH:mm:ss',
      { zone: 'utc' },
    )
      .set({ weekday: day })
      .toLocal()
      .toFormat('c');
    return Number(convertedWeekday);
  });
};

/**
 * Check if days match 'Weekday' template
 * @param {Array} weekdays
 */
const isOnWeekdays = (weekdays: number[]) => {
  const template = [1, 2, 3, 4, 5];
  return checkForWeekdayTemplate(weekdays, template);
};

/**
 * Check if days match 'Weekend' template
 * @param {Array} weekdays
 */
const isOnWeekends = (weekdays: number[]) => {
  const template = [6, 7];
  return checkForWeekdayTemplate(weekdays, template);
};

/**
 * Check if days match 'Daily' template
 * @param {Array} weekdays
 */
const isDaily = (weekdays: number[]) => {
  const template = [1, 2, 3, 4, 5, 6, 7];
  return checkForWeekdayTemplate(weekdays, template);
};

/**
 * Get string for recurring live event upcoming event subheader
 * @param {Array} weekdays
 */
const getWeekdayString = (weekdays: number[] = [], startTime: string = '') => {
  const convertedWeekdays = convertWeekdaysToLocal(weekdays, startTime).sort();

  /** Check if weekdays fit generic templates like 'Weekdays', 'Weekends', or 'Daily' */
  if (isOnWeekdays(convertedWeekdays)) {
    return T.weekdays;
  }

  if (isOnWeekends(convertedWeekdays)) {
    return T.weekends;
  }

  if (isDaily(convertedWeekdays)) {
    return T.daily;
  }

  /** Return a string based on the days of the week scheduled */
  const weekdayArray: string[] = [];

  convertedWeekdays.forEach((weekday) => {
    weekdayArray.push(DateTime.fromFormat(weekday.toString(), 'c').toFormat('cccc'));
  });

  const weekdayArrayLength = weekdayArray.length;

  switch (weekdayArrayLength) {
    case 0:
      return '';
    case 1:
      return `Every ${weekdayArray[0]}`;
    case 2:
      return `Every ${weekdayArray[0]} and ${weekdayArray[1]}`;
    default:
      return `Every ${weekdayArray.slice(0, weekdayArrayLength - 1).join(', ')} and ${
        weekdayArray[weekdayArrayLength - 1]
      }`;
  }
};

/**
 * Get localized date object from Luxon's DateTime
 * @param date
 */
const getLocalizedDateTime = (date: string) => {
  return DateTime.fromISO(date).toLocal();
};

const getLegacyScheduleTime = (schedule: Schedule): string => {
  const time = schedule.type === SINGLE_EVENT ? schedule.startTime : schedule.dailyTime;
  if (!time) {
    return T.notStartedYet;
  }

  switch (schedule.type) {
    case SINGLE_EVENT:
      if (schedule.startTime) {
        return getLocalizedDateTime(schedule.startTime).toFormat("LLLL d 'at' t");
      }
      return '';
    case WEEKLY_EVENT:
      if (schedule.dailyTime) {
        return `${getWeekdayString(
          schedule.weekdays,
          schedule.dailyTime,
        )} at ${getLocalizedDateTime(schedule.dailyTime).toFormat('t')}`;
      }
      return '';
    default:
      return '';
  }
};

const getRRuleInstanceForSchedule = (schedule: EventSchedule): RRule => {
  const opts = RRule.parseString(schedule.rrule as string);

  if (schedule.startTime) {
    opts.dtstart = new Date(schedule.startTime);
  }

  if (schedule.endTime) {
    opts.until = new Date(schedule.endTime);
  }

  return new RRule(opts);
};

const getNewScheduleTime = (schedule: EventSchedule): string => {
  if (schedule?.rrule) {
    const rr = getRRuleInstanceForSchedule(schedule);
    if (!rr.after(new Date())) {
      return T.schedulePlaceholder;
    }

    const timeString: string = rr.toText();
    const suffix = (schedule as EventSchedule).startTime
      ? getLocalizedDateTime(schedule.startTime as string).toFormat("'at' t")
      : '';

    return `${timeString[0].toUpperCase()}${timeString.slice(1)} ${suffix}`;
  }

  return schedule?.startTime
    ? getLocalizedDateTime(schedule.startTime).toFormat("LLLL d 'at' t")
    : T.schedulePlaceholder;
};

const getLegacyScheduleAvailability = (schedule: Schedule) => {
  const time = schedule.type === SINGLE_EVENT ? schedule.startTime : schedule.dailyTime;
  if (schedule && !time) {
    return T.watchLive;
  }
  return T.scheduled;
};

const getNewScheduleAvailability = (schedule: EventSchedule) => {
  if (schedule?.rrule) {
    const rr = getRRuleInstanceForSchedule(schedule);
    return !rr.after(new Date()) ? T.watchLive : T.scheduled;
  } else if (schedule?.startTime) {
    return T.scheduled;
  }
  return T.watchLive;
};

/**
 * Get schedule availability string
 * @param {Schedule} schedule
 */
export const getScheduleAvailability = (schedule: Schedule | EventSchedule | null) => {
  if (!schedule) {
    return T.watchLive;
  }

  if ((schedule as Schedule)?.type) {
    return getLegacyScheduleAvailability(schedule as Schedule);
  }

  return getNewScheduleAvailability(schedule as EventSchedule);
};

/**
 * Get schedule time based on date and weekdays from schedule object
 * @param {Schedule} schedule
 * @return {string}
 */
export const getScheduleTime = (schedule: Schedule | EventSchedule | null): string => {
  if (!schedule) {
    return T.schedulePlaceholder;
  }

  if ((schedule as Schedule)?.type) {
    return getLegacyScheduleTime(schedule as Schedule);
  }

  return getNewScheduleTime(schedule as EventSchedule);
};
